import React, { useEffect, useMemo, useState } from 'react'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  GET_ALL_FORM_BUILDER_BY_BG_ACCOUNT_ID,
  GET_PREFILL_RECIPIENTS
} from '../../../graphql/query'
import { REVOKE_PREFILL_RECIPIENT } from '../../../graphql/mutation'
import PrefillFormPreviewModal from './formBuilderModel/PrefillFormPreviewModal'
import SendPrefillLinksModal from './formBuilderModel/SendPrefillLinksModal'
import DeletePrefillRecipientModal from './formBuilderModel/DeletePrefillRecipientModal'
import { PREFILL_MAX_IMPORT_ROWS } from './formBuilderModel/prefillFieldUtils'
import './PrefillRecipientsPage.scss'

export interface PrefillDataEntry {
  fieldId: number
  label: string
  value: string
  editable: boolean
}

export interface PrefillRecipientRow {
  recipientUuid: string
  batchUuid: string
  recipientEmail: string
  recipientName: string | null
  shareableLink: string
  status: string
  sentMailCount?: number
  sentAt?: string | null
  submittedAt?: string | null
  saveFormUuid?: string | null
  prefillData?: PrefillDataEntry[]
  createdAt?: string | null
}

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const
const DEFAULT_PAGE_SIZE = 20

const formatDateTime = (value?: string | null) => {
  if (!value) return '—'
  return new Date(value).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

type MailRecipientMeta = Pick<PrefillRecipientRow, 'recipientUuid' | 'status' | 'sentMailCount'>

const hasBeenMailedBefore = (recipient: MailRecipientMeta) =>
  (recipient.sentMailCount ?? 0) > 0 ||
  ['sent', 'opened', 'failed', 'submitted'].includes(recipient.status)

const canSendMail = (recipient: MailRecipientMeta) =>
  !['revoked', 'submitted', 'queued'].includes(recipient.status)

const canResendMail = (recipient: MailRecipientMeta) =>
  recipient.status !== 'revoked' && recipient.status !== 'queued' && hasBeenMailedBefore(recipient)

const getBulkSendLabel = (count: number) => `Send mail${count > 0 ? ` (${count})` : ''}`

const getBulkResendLabel = (count: number) => `Resend mail${count > 0 ? ` (${count})` : ''}`

const canRevokeLink = (status: string) =>
  !['revoked', 'submitted', 'queued', 'revision_requested'].includes(status)

const MailStatus = ({ status }: { status: string }) => {
  const labels: Record<string, string> = {
    ready: 'Not sent',
    queued: 'Sending…',
    sent: 'Sent',
    opened: 'Opened',
    failed: 'Failed',
    submitted: 'Submitted',
    revision_requested: 'Revision requested',
    revoked: 'Revoked'
  }
  return (
    <span className={`prefill-recipients-page__status prefill-recipients-page__status--${status}`}>
      {labels[status] || status}
    </span>
  )
}

const PrefillRecipientsPage: React.FC = () => {
  const { uuid } = useParams()
  const navigate = useNavigate()
  const apolloClient = useApolloClient()
  const [selectedUuids, setSelectedUuids] = useState<string[]>([])
  const [sendModalOpen, setSendModalOpen] = useState(false)
  const [sendModalMode, setSendModalMode] = useState<'send' | 'resend'>('send')
  const [sendTargetUuids, setSendTargetUuids] = useState<string[]>([])
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [previewRecipient, setPreviewRecipient] = useState<PrefillRecipientRow | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<PrefillRecipientRow | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null)
  const [actionMenuPos, setActionMenuPos] = useState<{
    top?: number
    bottom?: number
    right: number
  } | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE)
  const [exporting, setExporting] = useState(false)

  const closeActionMenu = () => {
    setOpenActionMenu(null)
    setActionMenuPos(null)
  }

  const toggleActionMenu = (recipientUuid: string, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (openActionMenu === recipientUuid) {
      closeActionMenu()
      return
    }

    const rect = event.currentTarget.getBoundingClientRect()
    const menuHeight = 240
    const openUpward = window.innerHeight - rect.bottom < menuHeight && rect.top > menuHeight
    setActionMenuPos(
      openUpward
        ? { bottom: window.innerHeight - rect.top + 4, right: window.innerWidth - rect.right }
        : { top: rect.bottom + 4, right: window.innerWidth - rect.right }
    )
    setOpenActionMenu(recipientUuid)
  }

  useEffect(() => {
    if (!openActionMenu) return undefined
    const handleClose = () => closeActionMenu()
    window.addEventListener('scroll', handleClose, true)
    window.addEventListener('resize', handleClose)
    return () => {
      window.removeEventListener('scroll', handleClose, true)
      window.removeEventListener('resize', handleClose)
    }
  }, [openActionMenu])

  useEffect(() => {
    setCurrentPage(1)
    setSelectedUuids([])
  }, [statusFilter, pageSize, uuid])

  const queryOffset = (currentPage - 1) * pageSize

  const { data: formData } = useQuery(GET_ALL_FORM_BUILDER_BY_BG_ACCOUNT_ID, {
    variables: { input: { formUuid: uuid } },
    context: { headers: { 'access-control-allow-methods': 'Form-Builder' } },
    skip: !uuid
  })

  const { data, loading, refetch } = useQuery(GET_PREFILL_RECIPIENTS, {
    variables: {
      input: {
        formUuid: uuid,
        limit: pageSize,
        offset: queryOffset,
        statusFilter: statusFilter === 'all' ? undefined : statusFilter
      }
    },
    context: { headers: { 'access-control-allow-methods': 'Form-Builder' } },
    skip: !uuid,
    fetchPolicy: 'cache-and-network'
  })

  const formRecord = formData?.getAllFormBuilderByBgAccountId?.data?.[0]
  const formName = formRecord?.name || 'Form'
  const recipients: PrefillRecipientRow[] = data?.getPrefillRecipients?.recipients || []
  const filteredTotal = data?.getPrefillRecipients?.total ?? 0
  const stats = data?.getPrefillRecipients?.stats
  const totalPages = Math.max(1, Math.ceil(filteredTotal / pageSize))
  const hasQueuedRecipients =
    (stats?.queued ?? 0) > 0 || recipients.some((r) => r.status === 'queued')
  const rangeStart = filteredTotal === 0 ? 0 : queryOffset + 1
  const rangeEnd = Math.min(queryOffset + recipients.length, filteredTotal)

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  useEffect(() => {
    if (!hasQueuedRecipients) return undefined
    const timer = setInterval(() => {
      refetch()
    }, 5000)
    return () => clearInterval(timer)
  }, [hasQueuedRecipients, refetch])

  const mailStats = useMemo(
    () => ({
      pending: stats?.pending ?? 0,
      queued: stats?.queued ?? 0,
      sent: stats?.sent ?? 0,
      submitted: stats?.submitted ?? 0,
      failed: stats?.failed ?? 0
    }),
    [stats]
  )

  const statusFilters = [
    { id: 'all', label: 'All', count: stats?.total ?? filteredTotal },
    { id: 'pending', label: 'Pending', count: mailStats.pending },
    ...(mailStats.queued > 0 ? [{ id: 'queued', label: 'Sending', count: mailStats.queued }] : []),
    { id: 'sent', label: 'Sent', count: mailStats.sent },
    { id: 'submitted', label: 'Submitted', count: mailStats.submitted },
    ...(mailStats.failed > 0 ? [{ id: 'failed', label: 'Failed', count: mailStats.failed }] : [])
  ]

  const getRecipientMeta = (recipientUuid: string): MailRecipientMeta | undefined => {
    const r = recipients.find((x) => x.recipientUuid === recipientUuid)
    if (!r) return undefined
    return {
      recipientUuid: r.recipientUuid,
      status: r.status,
      sentMailCount: r.sentMailCount
    }
  }

  const resendableSelected = selectedUuids.filter((id) => {
    const meta = getRecipientMeta(id)
    return meta && canResendMail(meta)
  })

  const sendableSelected = selectedUuids.filter((id) => {
    const meta = getRecipientMeta(id)
    return meta && canSendMail(meta) && !hasBeenMailedBefore(meta)
  })

  const openEmailModal = (uuids: string[], mode?: 'send' | 'resend') => {
    const valid = uuids
      .map(getRecipientMeta)
      .filter((r): r is MailRecipientMeta => Boolean(r))
      .filter((r) => {
        if (mode === 'resend') return canResendMail(r)
        if (mode === 'send') return canSendMail(r) && !hasBeenMailedBefore(r)
        return canSendMail(r) || canResendMail(r)
      })
    if (valid.length === 0) {
      toast.error(
        mode === 'resend'
          ? 'Select recipients who were already mailed and are not revoked.'
          : 'Select recipients who have not been mailed yet and are not submitted or revoked.'
      )
      return
    }
    setSendTargetUuids(valid.map((r) => r.recipientUuid))
    setSendModalMode(mode ?? (valid.some(hasBeenMailedBefore) ? 'resend' : 'send'))
    setSendModalOpen(true)
  }

  const [revokeRecipient] = useMutation(REVOKE_PREFILL_RECIPIENT, {
    context: { headers: { 'access-control-allow-methods': 'Form-Builder' } }
  })

  const openDeleteModal = (recipient: PrefillRecipientRow) => {
    closeActionMenu()
    setDeleteTarget(recipient)
  }

  const handleRecipientDeleted = (recipientUuid: string) => {
    setSelectedUuids((prev) => prev.filter((id) => id !== recipientUuid))
    refetch()
  }

  const toggleAll = (checked: boolean) => {
    setSelectedUuids(checked ? recipients.map((r) => r.recipientUuid) : [])
  }

  const toggleOne = (recipientUuid: string, checked: boolean) => {
    if (checked) {
      setSelectedUuids([...selectedUuids, recipientUuid])
    } else {
      setSelectedUuids(selectedUuids.filter((id) => id !== recipientUuid))
    }
  }

  const handleRevoke = async (recipientUuid: string) => {
    setActionLoading(recipientUuid)
    try {
      const { data: result } = await revokeRecipient({ variables: { input: { recipientUuid } } })
      if (result?.revokePrefillRecipient?.success) {
        toast.success('Link revoked')
        refetch()
      } else {
        toast.error(result?.revokePrefillRecipient?.message || 'Revoke failed')
      }
    } catch (err: any) {
      toast.error(err.message || 'Revoke failed')
    } finally {
      setActionLoading(null)
    }
  }

  const handleDelete = (recipient: PrefillRecipientRow) => {
    openDeleteModal(recipient)
  }

  const handleExportCsv = async () => {
    if (!uuid || exporting) return
    setExporting(true)
    try {
      const { data: exportData } = await apolloClient.query({
        query: GET_PREFILL_RECIPIENTS,
        variables: {
          input: {
            formUuid: uuid,
            limit: PREFILL_MAX_IMPORT_ROWS,
            offset: 0,
            statusFilter: statusFilter === 'all' ? undefined : statusFilter
          }
        },
        context: { headers: { 'access-control-allow-methods': 'Form-Builder' } },
        fetchPolicy: 'network-only'
      })

      const exportRecipients: PrefillRecipientRow[] =
        exportData?.getPrefillRecipients?.recipients || []
      if (exportRecipients.length === 0) {
        toast.info('No recipients to export')
        return
      }

      const header = [
        'recipient_name',
        'recipient_email',
        'shareable_link',
        'mail_status',
        'mails_sent_count',
        'created_at',
        'sent_at'
      ]
      const lines = exportRecipients.map((r) =>
        [
          r.recipientName || '',
          r.recipientEmail,
          r.shareableLink,
          r.status,
          String(r.sentMailCount || 0),
          r.createdAt || '',
          r.sentAt || ''
        ]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(',')
      )
      const csv = [header.join(','), ...lines].join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `prefill_recipients_${uuid}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err: any) {
      toast.error(err.message || 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  const goToPage = (page: number) => {
    setCurrentPage(Math.min(Math.max(page, 1), totalPages))
    closeActionMenu()
  }

  const allFilteredSelected =
    recipients.length > 0 && recipients.every((r) => selectedUuids.includes(r.recipientUuid))

  const hasAnyRecipients = (stats?.total ?? 0) > 0

  return (
    <div className='prefill-recipients-page' onClick={closeActionMenu}>
      <header className='prefill-recipients-page__header'>
        <button
          type='button'
          className='prefill-recipients-page__back'
          onClick={() => navigate(`/automation/process-automation/form-builder/share-form/${uuid}`)}
          aria-label='Back'
        >
          <i className='bi bi-arrow-left' />
        </button>
        <div className='flex-grow-1'>
          <div className='prefill-recipients-page__title-row'>
            <h1 className='prefill-recipients-page__title'>Recipient Forms</h1>
            <span className='prefill-recipients-page__badge'>Prefilled</span>
          </div>
          <p className='prefill-recipients-page__subtitle'>{formName}</p>
        </div>
        <div className='prefill-recipients-page__header-actions'>
          <button
            type='button'
            className='btn btn-light'
            onClick={() => navigate(`/automation/table/${uuid}`)}
          >
            <i className='bi bi-table me-1' />
            Submissions
          </button>
          <button
            type='button'
            className='prefill-recipients-page__icon-btn'
            onClick={() => refetch()}
            title='Refresh'
          >
            <i className='bi bi-arrow-clockwise' />
          </button>
        </div>
      </header>

      <div className='prefill-recipients-page__panel'>
        <div className='prefill-recipients-page__toolbar prefill-recipients-page__toolbar--no-search'>
          <div className='prefill-recipients-page__actions'>
            <button
              type='button'
              className='btn btn-outline-secondary btn-sm'
              disabled={!hasAnyRecipients || exporting}
              onClick={handleExportCsv}
            >
              <i className={`bi ${exporting ? 'bi-hourglass-split' : 'bi-download'} me-1`} />
              {exporting ? 'Exporting…' : 'Export'}
            </button>
            {selectedUuids.length > 0 && sendableSelected.length > 0 && (
              <button
                type='button'
                className='btn btn-outline-primary btn-sm'
                onClick={() => openEmailModal(sendableSelected, 'send')}
              >
                <i className='bi bi-envelope me-1' />
                {getBulkSendLabel(sendableSelected.length)}
              </button>
            )}
            {selectedUuids.length > 0 && resendableSelected.length > 0 && (
              <button
                type='button'
                className='btn btn-primary btn-sm'
                onClick={() => openEmailModal(resendableSelected, 'resend')}
              >
                <i className='bi bi-arrow-repeat me-1' />
                {getBulkResendLabel(resendableSelected.length)}
              </button>
            )}
          </div>
        </div>

        {!loading && hasAnyRecipients && (
          <div className='prefill-recipients-page__filters'>
            {statusFilters.map((filter) => (
              <button
                key={filter.id}
                type='button'
                className={`prefill-recipients-page__filter ${
                  statusFilter === filter.id ? 'prefill-recipients-page__filter--active' : ''
                }`}
                onClick={() => setStatusFilter(filter.id)}
              >
                {filter.label}
                <span className='prefill-recipients-page__filter-count'>{filter.count}</span>
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className='prefill-recipients-page__loading'>
            <div className='spinner-border spinner-border-sm text-primary me-2' role='status' />
            Loading recipients...
          </div>
        ) : !hasAnyRecipients ? (
          <div className='prefill-recipients-page__empty'>
            <i className='bi bi-inbox d-block' />
            <p className='mb-2 fw-medium'>No recipient forms yet</p>
            <p className='mb-3 small'>
              Upload a filled Excel file from the Share page to create one personalized form per
              row.
            </p>
            <button
              type='button'
              className='btn btn-primary'
              onClick={() =>
                navigate(`/automation/process-automation/form-builder/share-form/${uuid}`)
              }
            >
              Go to Share page
            </button>
          </div>
        ) : recipients.length === 0 ? (
          <div className='prefill-recipients-page__empty'>
            <i className='bi bi-funnel d-block' />
            <p className='mb-1 fw-medium'>No matching recipients</p>
            <p className='mb-0 small'>No recipients in this status filter.</p>
          </div>
        ) : (
          <>
            <div className='prefill-recipients-page__table-wrap'>
              <table className='prefill-recipients-page__table'>
                <thead>
                  <tr>
                    <th style={{ width: 44 }}>
                      <input
                        type='checkbox'
                        className='form-check-input'
                        checked={allFilteredSelected}
                        onChange={(e) => toggleAll(e.target.checked)}
                      />
                    </th>
                    <th>Name</th>
                    <th>Email</th>
                    <th style={{ minWidth: 180 }}>Share link</th>
                    <th style={{ width: 120 }}>Status</th>
                    <th style={{ width: 150 }}>Created</th>
                    <th style={{ minWidth: 150 }}>Mail</th>
                    <th style={{ width: 52 }} className='text-end' />
                  </tr>
                </thead>
                <tbody>
                  {recipients.map((r) => (
                    <tr key={r.recipientUuid}>
                      <td>
                        <input
                          type='checkbox'
                          className='form-check-input'
                          checked={selectedUuids.includes(r.recipientUuid)}
                          onChange={(e) => toggleOne(r.recipientUuid, e.target.checked)}
                        />
                      </td>
                      <td>
                        <span className='prefill-recipients-page__recipient-name'>
                          {r.recipientName || '—'}
                        </span>
                      </td>
                      <td>
                        <span className='prefill-recipients-page__recipient-email'>
                          {r.recipientEmail}
                        </span>
                      </td>
                      <td>
                        {r.shareableLink ? (
                          <div className='prefill-recipients-page__form-link'>
                            <a
                              href={r.shareableLink}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='prefill-recipients-page__form-link-url'
                              title={r.shareableLink}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {r.shareableLink}
                            </a>
                            <button
                              type='button'
                              className='prefill-recipients-page__icon-btn'
                              title='Copy link'
                              aria-label='Copy form link'
                              onClick={(e) => {
                                e.stopPropagation()
                                navigator.clipboard.writeText(r.shareableLink)
                                toast.success('Link copied')
                              }}
                            >
                              <i className='bi bi-clipboard' />
                            </button>
                          </div>
                        ) : (
                          <span className='text-muted small'>—</span>
                        )}
                      </td>
                      <td>
                        <MailStatus status={r.status} />
                      </td>
                      <td>
                        <span className='prefill-recipients-page__created-at'>
                          {formatDateTime(r.createdAt)}
                        </span>
                      </td>
                      <td>
                        <div className='prefill-recipients-page__mail-actions'>
                          {canSendMail(r) && !hasBeenMailedBefore(r) ? (
                            <button
                              type='button'
                              className='btn btn-sm btn-primary'
                              disabled={actionLoading === r.recipientUuid}
                              onClick={(e) => {
                                e.stopPropagation()
                                openEmailModal([r.recipientUuid], 'send')
                              }}
                            >
                              <i className='bi bi-envelope me-1' />
                              Send mail
                            </button>
                          ) : null}
                          {canResendMail(r) ? (
                            <button
                              type='button'
                              className='btn btn-sm btn-outline-primary'
                              disabled={actionLoading === r.recipientUuid}
                              onClick={(e) => {
                                e.stopPropagation()
                                openEmailModal([r.recipientUuid], 'resend')
                              }}
                            >
                              <i
                                className={`bi ${
                                  r.status === 'queued' ? 'bi-hourglass-split' : 'bi-arrow-repeat'
                                } me-1`}
                              />
                              {r.status === 'queued' ? 'Retry mail' : 'Resend mail'}
                            </button>
                          ) : null}
                          {!((canSendMail(r) && !hasBeenMailedBefore(r)) || canResendMail(r)) ? (
                            <span className='text-muted small'>—</span>
                          ) : null}
                        </div>
                      </td>
                      <td className='text-end'>
                        <div
                          className='prefill-recipients-page__action-menu'
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type='button'
                            className='prefill-recipients-page__icon-btn'
                            title='More actions'
                            onClick={(e) => toggleActionMenu(r.recipientUuid, e)}
                          >
                            <i className='bi bi-three-dots-vertical' />
                          </button>
                          {openActionMenu === r.recipientUuid && actionMenuPos ? (
                            <div
                              className='prefill-recipients-page__dropdown'
                              style={{
                                top: actionMenuPos.top,
                                bottom: actionMenuPos.bottom,
                                right: actionMenuPos.right
                              }}
                            >
                              <button
                                type='button'
                                onClick={() => {
                                  if (!formRecord?.formData) {
                                    toast.error('Form data is still loading.')
                                    return
                                  }
                                  setPreviewRecipient(r)
                                  closeActionMenu()
                                }}
                              >
                                <i className='bi bi-eye' />
                                Preview form
                              </button>
                              {r.shareableLink ? (
                                <button
                                  type='button'
                                  onClick={() => {
                                    navigator.clipboard.writeText(r.shareableLink)
                                    toast.success('Link copied')
                                    closeActionMenu()
                                  }}
                                >
                                  <i className='bi bi-link-45deg' />
                                  Copy link
                                </button>
                              ) : null}
                              {r.status === 'submitted' ? (
                                <button
                                  type='button'
                                  onClick={() => {
                                    navigate(`/automation/table/${uuid}`)
                                    closeActionMenu()
                                  }}
                                >
                                  <i className='bi bi-file-earmark-check' />
                                  View submission
                                </button>
                              ) : null}
                              {canRevokeLink(r.status) ? (
                                <button
                                  type='button'
                                  className='prefill-recipients-page__dropdown-danger'
                                  disabled={actionLoading === r.recipientUuid}
                                  onClick={() => {
                                    closeActionMenu()
                                    handleRevoke(r.recipientUuid)
                                  }}
                                >
                                  <i className='bi bi-slash-circle' />
                                  Revoke link
                                </button>
                              ) : null}
                              <button
                                type='button'
                                className='prefill-recipients-page__dropdown-danger'
                                disabled={actionLoading === r.recipientUuid}
                                onClick={() => handleDelete(r)}
                              >
                                <i className='bi bi-trash' />
                                Delete recipient
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className='prefill-recipients-page__footer'>
              <div className='prefill-recipients-page__footer-info'>
                {rangeStart}–{rangeEnd} of {filteredTotal}
              </div>
              <div className='prefill-recipients-page__footer-controls'>
                <label className='prefill-recipients-page__page-size'>
                  Rows per page
                  <select
                    className='form-select form-select-sm'
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
                <ul className='pagination pagination-sm mb-0'>
                  <li className={`page-item ${currentPage <= 1 ? 'disabled' : ''}`}>
                    <button
                      type='button'
                      className='page-link'
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage <= 1}
                    >
                      Previous
                    </button>
                  </li>
                  <li className='page-item disabled'>
                    <span className='page-link'>
                      Page {currentPage} of {totalPages}
                    </span>
                  </li>
                  <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
                    <button
                      type='button'
                      className='page-link'
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </>
        )}
      </div>

      {previewRecipient && formRecord?.formData && (
        <PrefillFormPreviewModal
          isOpen={!!previewRecipient}
          onClose={() => setPreviewRecipient(null)}
          formData={formRecord.formData}
          cardStyle={formRecord.cardStyle || formRecord.cardstyle || ''}
          cardBackground={formRecord.cardBackground || ''}
          prefillData={previewRecipient.prefillData}
          shareableLink={previewRecipient.shareableLink}
          recipientEmail={previewRecipient.recipientEmail}
          recipientName={previewRecipient.recipientName}
        />
      )}

      <SendPrefillLinksModal
        isOpen={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        recipientUuids={sendTargetUuids}
        mode={sendModalMode}
        onSent={() => {
          refetch()
          setSelectedUuids([])
          setSendTargetUuids([])
        }}
      />

      <DeletePrefillRecipientModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        recipient={deleteTarget}
        onDeleted={handleRecipientDeleted}
      />
    </div>
  )
}

export default PrefillRecipientsPage
